import type { Feature, FeatureCollection, Geometry, Point, Polygon, Position } from 'geojson'
import type { GeoJSONSource, Map as MapLibreMap, MapSourceDataEvent } from 'maplibre-gl'
import { despiked } from './despike'
import { childFreeAnchor, labelShape, nestsIn, type LabelShape } from './label_anchor'
import { SLOT_BAND, SLOT_HIGHLIGHT, SLOT_LABEL, SLOT_LINE, SLOT_TINT } from './slots'
import {
  COUNTRY_KIND,
  MAX_ZOOM,
  REGION_ARRIVAL_FADE_MS,
  REGION_FILL_COLOR,
  REGION_FILL_LAYER_PREFIX,
  REGION_HOVER_COLOR,
  REGION_LABEL_CAPS_FONT,
  REGION_LABEL_COLOR,
  REGION_LABEL_FONT,
  REGION_LABEL_GREAT_ROOM,
  REGION_LABEL_HALO_COLOR,
  REGION_LABEL_LAYER_ID,
  REGION_LABEL_PADDING_STOPS,
  REGION_LABEL_SOURCE_ID,
  REGION_LABEL_WIDE_ROOM,
  REGION_LINE_COLOR,
  REGION_LINE_HOVER_WIDTH,
  REGION_LINE_LAYER_PREFIX,
  REGION_LINE_NEAR_OPACITY,
  REGION_LINE_NEAR_ZOOMS,
  REGION_LINE_OPACITY,
  REGION_LINE_WIDTH,
  REGION_LINE_WIDTH_ZOOMS,
  REGION_REVEAL_ZOOMS,
  REGION_RIBBON_HOVER_COLOR,
  REGION_RIBBON_HOVER_GROW,
  REGION_RIBBON_LAYER_ID,
  REGION_RIBBON_LAYER_OPACITY,
  REGION_RIBBON_ROOM_SHARE,
  REGION_SOURCE_ID,
  SELECTED_CASING_COLOR,
  SELECTED_CASING_LAYER_ID,
  SELECTED_CASING_WIDTH_STOPS,
  SELECTED_FILL_COLOR,
  SELECTED_GLOW_LAYER_ID,
  SELECTED_GLOW_OPACITY,
  SELECTED_GLOW_WIDTH_STOPS,
  SELECTED_LINE_COLOR,
  SELECTED_LINE_LAYER_ID,
  SELECTED_LINE_WIDTH_STOPS,
  SELECTED_PART_VEIL,
  SELECTED_RIBBON_LAYER_ID,
  SELECTED_RIBBON_WIDTH_STOPS,
  SELECTED_SOURCE_ID,
  SELECTED_VEIL_COLOR,
  SELECTED_VEIL_LAYER_ID,
  SELECTED_VEIL_OPACITY,
  regionFillStops,
  regionHoverFillStops,
  regionRibbonWidth,
  ribbonDepth,
} from '../../lib/map_config'
import type { DetailFeature, DetailLayer } from '../../types/atlas'

/**
 * The appellation layer: a polygon source, a point source for the names, one
 * ribbon layer and one name layer for every tier, and two layers per tier — a
 * fill and a hairline.
 *
 * It is drawn the way a printed wine atlas draws one, and it lays no film on
 * the ground. An appellation is a claret hairline, and inside the hairline a
 * RIBBON: a flat band hugging the boundary, the hand-tinted edge of an atlas
 * plate. Depth is ribbons inside ribbons — each level of nesting is narrower
 * and stronger — and every depth is drawn by one line layer through
 * `line-layer-opacity`, so a ribbon never prints over itself (`REGION_RIBBON`
 * in map_config.ts has the reasoning, and what this replaced). The name is set
 * once, in italic, at a point chosen to be inside the polygon and, for a parent,
 * clear of its children.
 *
 * The FILL still exists, per tier, and mostly draws nothing. It is what a click
 * and the pointer are tested against, so it stays in the style at every zoom;
 * it carries a faint tint for the first couple of levels after a tier appears,
 * while a shape is too small for a ribbon to say anything, and a wash under the
 * pointer while the shape is still a shape (`regionFillStops`).
 *
 * The per-tier reveal is MapLibre's own `minzoom`, read from each feature's
 * `min_zoom` property — so a fetch that lands at zoom 4 also covers 5 and 7 for
 * the features it carries, and each step adds the next tier with no further
 * round trip. Every opacity is additionally a zoom ramp from 0 at the tier, so
 * the camera scrubs the reveal rather than a tier popping in between two frames.
 *
 * Tiers arrive over time, not all at once. Under KTD5's viewport fetching the
 * first response carries only `min_zoom <= zoom`, so a reader zooming in meets
 * tiers 5 and 7 in LATER responses — which is why `addRegionLayers` adds the
 * layers for any tier it has not seen rather than building them all once.
 *
 * A tier is a layer rather than a `["zoom"]` filter on one layer because a zoom
 * expression inside a filter is evaluated only at integer zoom levels, which
 * would make the reveal fire at a different moment than the camera crosses it.
 * The tiers themselves come from the data, never from a second copy of 4, 5 and
 * 7 written down here — and so does DEPTH: an appellation's level of nesting is
 * the rank of its tier among the tiers this map has been handed.
 *
 * Every layer is inserted before a slot (slots.ts), never appended: a tier that
 * arrives after the winery dots and the journal's pins were added must still be
 * drawn under them.
 *
 * WHAT IT COSTS PER FRAME. One offscreen pass, for the ribbon layer, for as
 * long as an appellation is on screen: that is what `line-layer-opacity` below
 * 1 means. It is the price of ribbons that replace rather than add, and it is
 * the only per-frame work this drawing has that the last one did not.
 *
 * Types are imported from maplibre-gl, values never: this module is reached only
 * through use_maplibre.ts, which is itself behind the layout's dynamic import.
 */

export function regionTiers(layer: DetailLayer): number[] {
  const tiers = new Set<number>()

  for (const feature of layer.features) {
    const tier = feature.properties.min_zoom
    if (typeof tier === 'number') tiers.add(tier)
  }

  return [...tiers].sort((first, second) => first - second)
}

export function regionFillLayerId(tier: number) {
  return `${REGION_FILL_LAYER_PREFIX}-${tier}`
}

export function regionLineLayerId(tier: number) {
  return `${REGION_LINE_LAYER_PREFIX}-${tier}`
}

// Every layer of every tier and the two layers they share — the ribbons and
// the names — which is what a visibility toggle has to reach.
export function regionLayerIds(tiers: number[]): string[] {
  if (tiers.length === 0) return []

  return [
    ...tiers.flatMap((tier) => [regionFillLayerId(tier), regionLineLayerId(tier)]),
    REGION_RIBBON_LAYER_ID,
    REGION_LABEL_LAYER_ID,
  ]
}

// The layers a click or a hover is scoped to. Labels are excluded: a symbol
// layer's hit area is the glyph, not the polygon, so including it would make the
// cursor flicker over a name that sits outside its own region. The ribbon and
// the hairline are excluded because the fill already covers every pixel they
// do — drawn or not: a fill whose opacity is 0 is still found under the pointer.
export function regionInteractiveLayerIds(tiers: number[]): string[] {
  return tiers.map(regionFillLayerId)
}

// A feature whose tier could not be read. It sorts below every real tier rather
// than throwing or winning by arriving first: `min_zoom` is nullable in
// DetailFeatureProperties, and the country layer carries no such property at all.
const UNTIERED = Number.NEGATIVE_INFINITY

interface TieredFeature {
  properties?: Record<string, unknown> | null
}

function featureDepth(feature: TieredFeature): number {
  const tier = feature.properties?.min_zoom

  return typeof tier === 'number' && Number.isFinite(tier) ? tier : UNTIERED
}

/**
 * Which of the features under a pointer is the one meant (KTD13).
 *
 * Candiago's boundaries are municipality unions and nest by construction —
 * Bordeaux, Médoc, Haut-Médoc and Margaux all cover the same communes — so
 * `queryRenderedFeatures` hands a click near Margaux four hits, and taking
 * `features[0]` takes whatever render order happened to put first. The winner is
 * the highest `min_zoom`: the deepest tier, which is the cheap proxy for the
 * smallest area and needs no area computation on the client. P6's
 * point-in-polygon winery matching applies the same rule server-side.
 *
 * Ties keep the first hit, so the same pixel never resolves two ways — two
 * appellations at one depth do overlap along the edges of their commune sets,
 * and an arbitrary winner there is a place page that changes under a re-click.
 *
 * Structurally typed rather than taking `MapGeoJSONFeature`, so it is testable
 * without standing up a map.
 */
export function resolveFeature<Feature extends TieredFeature>(
  features: readonly Feature[] | undefined,
): Feature | null {
  let winner: Feature | null = null
  let winningDepth = UNTIERED

  for (const feature of features ?? []) {
    const depth = featureDepth(feature)
    if (winner && depth <= winningDepth) continue

    winner = feature
    winningDepth = depth
  }

  return winner
}

// ---------------------------------------------------------------------------
// Where a name goes
// ---------------------------------------------------------------------------

// Re-exported: the anchor is this layer's, and label_anchor.ts is where its
// arithmetic lives.
export { labelAnchor } from './label_anchor'

type LabelProperties = { slug: string; name: string; min_zoom: number | null; room: number }
type LabelFeature = Feature<Point, LabelProperties>

// What the map knows about one appellation's name: its shape as lettering sees
// it, where the name is now, and which children it was last placed clear of.
interface Lettering {
  feature: DetailFeature
  shape: LabelShape | null
  tier: number
  at: Position
  room: number
  // The children the name was last placed among, as a signature; null until
  // the child-free search has looked at it once.
  among: string | null
}

// Three decimals of `room` is a hundred metres: finer than any rank cares
// about, and it keeps a label feature's properties short.
const roundRoom = (room: number) => Math.round(room * 1000) / 1000

function letteringOf(feature: DetailFeature, previous: Lettering | undefined): Lettering | null {
  const tier = featureDepth(feature)

  if (feature.geometry.type === 'Point') {
    return { feature, shape: null, tier, at: feature.geometry.coordinates, room: 0, among: null }
  }

  const shape = labelShape(feature.geometry)
  if (!shape) return null

  // The same place, redrawn — the finer outline arriving on the way in, say.
  // Its name stays where the child-free search last put it until that search
  // has looked at the new outline: a parent's name that jumped back to its
  // centre and out again on every zoom would be worse than one a few pixels
  // from where it will settle.
  const at = previous?.among && nestsIn({ ...shape, at: previous.at }, shape) ? previous.at : shape.at

  return { feature, shape, tier, at, room: roundRoom(shape.room), among: null }
}

// Only what the label layers read. `min_zoom` rides along because each label
// layer is filtered to its tier exactly as the fill is, so a name never appears
// for a region that is not drawn; `room` is what ranks it.
function labelFeature({ feature, at, room }: Lettering): LabelFeature {
  const { slug, name, min_zoom } = feature.properties

  return { type: 'Feature', properties: { slug, name, min_zoom, room }, geometry: { type: 'Point', coordinates: at } }
}

// ---------------------------------------------------------------------------
// What the source holds, and how it changes
// ---------------------------------------------------------------------------

/**
 * A change to the region source rather than a whole new one: features to add
 * (or to replace, by slug) and slugs to drop. It is `GeoJSONSource#updateData`'s
 * own shape, narrowed to what a pan produces.
 */
export type RegionDiff = { add?: DetailFeature[]; remove?: string[] }

// What each map's region source currently holds, by slug, and what follows from
// it: the names, the tiers the ribbon has been drawn for, and the parents whose
// names are waiting to be moved clear of their children.
//
// It is what makes a full collection diffable: the shell hands over the whole
// merged set after every pan, and this is how much of it the source already has.
//
// Keyed weakly by the map, so it needs no teardown, and rebuilt whenever the
// source is — a style swap discards the source and this with it.
interface Held {
  features: Map<string, DetailFeature>
  names: Map<string, Lettering>
  tiers: number[]
  waiting: Set<string>
  scheduled: boolean
}

const held = new WeakMap<MapLibreMap, Held>()

function sameCoordinates(first: unknown, second: unknown): boolean {
  if (first === second) return true
  if (!Array.isArray(first) || !Array.isArray(second) || first.length !== second.length) return false

  for (let index = 0; index < first.length; index++) {
    if (!sameCoordinates(first[index], second[index])) return false
  }

  return true
}

// Whether two features would draw the same. Compared by value, because the
// shell replaces a held feature with the new response's copy every time the
// server sends it again: by identity, every feature on screen would look changed
// after every pan, and the diff would be the whole viewport.
function sameFeature(first: DetailFeature, second: DetailFeature): boolean {
  if (first === second) return true

  const before = first.properties as Record<string, unknown>
  const after = second.properties as Record<string, unknown>
  const keys = Object.keys(after)
  if (keys.length !== Object.keys(before).length || keys.some((key) => before[key] !== after[key])) return false

  if (first.geometry.type !== second.geometry.type) return false
  if (first.geometry.type === 'GeometryCollection' || second.geometry.type === 'GeometryCollection') return false

  return sameCoordinates(first.geometry.coordinates, second.geometry.coordinates)
}

/** What has to change for a source holding `current` to hold `next` instead. */
export function regionDiff(current: ReadonlyMap<string, DetailFeature>, next: DetailLayer): Required<RegionDiff> {
  const add: DetailFeature[] = []
  const kept = new Set<string>()

  for (const feature of next.features) {
    const existing = current.get(feature.properties.slug)
    kept.add(feature.properties.slug)
    if (!existing || !sameFeature(existing, feature)) add.push(feature)
  }

  const remove = [...current.keys()].filter((slug) => !kept.has(slug))

  return { add, remove }
}

function isCollection(data: DetailLayer | RegionDiff): data is DetailLayer {
  return 'features' in data
}

// A fresh record of a source that has just been handed `layer` whole. `before`
// is what it held until now, if anything: a place that is in both keeps its
// name where it was, and the ribbon is still drawn for the tiers it was drawn
// for.
function holding(layer: DetailLayer, before?: Held): Held {
  const state: Held = {
    features: new Map(),
    names: new Map(),
    tiers: before?.tiers ?? [],
    waiting: new Set(),
    scheduled: false,
  }

  for (const feature of layer.features) {
    const { slug } = feature.properties
    const lettering = letteringOf(feature, before?.names.get(slug))

    state.features.set(slug, feature)
    // Every name in a whole new set is to be looked at: there is no telling
    // yet whose child anything is, and asking each against each here would be
    // the search itself, run where the data arrives.
    if (lettering) {
      state.names.set(slug, lettering)
      if (lettering.shape) state.waiting.add(slug)
    }
  }

  return state
}

// Records one feature as held, and its name with it. Returns the name, or null
// for a geometry with nowhere to put one.
function take(state: Held, feature: DetailFeature) {
  const { slug } = feature.properties
  const lettering = letteringOf(feature, state.names.get(slug))

  state.features.set(slug, feature)
  if (lettering) state.names.set(slug, lettering)
  else state.names.delete(slug)

  if (lettering) disturb(state, lettering)

  return lettering
}

function drop(state: Held, slug: string) {
  const lettering = state.names.get(slug)

  state.features.delete(slug)
  state.names.delete(slug)
  state.waiting.delete(slug)
  if (lettering) disturb(state, lettering, false)
}

const overlap = (first: LabelShape, second: LabelShape) =>
  first.box.west < second.box.east &&
  first.box.east > second.box.west &&
  first.box.south < second.box.north &&
  first.box.north > second.box.south

// A name arrived, changed or left: whoever might have it as a child has to be
// looked at again, and so does it. By bounding box, which is all this can
// afford to ask in the same breath as the data arriving — the search itself
// decides who really nests in whom.
function disturb(state: Held, changed: Lettering, present = true) {
  if (!changed.shape) return
  if (present) state.waiting.add(changed.feature.properties.slug)

  for (const [slug, other] of state.names) {
    if (other === changed || !other.shape || other.tier >= changed.tier) continue
    if (overlap(other.shape, changed.shape)) state.waiting.add(slug)
  }
}

function labelCollection(state: Held): FeatureCollection<Point, LabelProperties> {
  return { type: 'FeatureCollection', features: [...state.names.values()].map(labelFeature) }
}

/**
 * Puts a region set, or a change to one, into the source.
 *
 * Handed a WHOLE collection — which is what the shell has after every pan — it
 * works out what actually changed against what the source holds and sends only
 * that: `updateData` re-tiles the tiles a diff touches, where `setData` posts
 * every feature to the worker and reloads every tile of the source (118 ms at
 * about 1,700 features, against 17 ms for a diff that misses the screen). A pan
 * that brought nothing new sends nothing at all.
 *
 * Handed a DIFF, it applies it as given. Nothing in the shell produces one yet;
 * when the merge learns to (it already knows both halves — what a response
 * added and what the cap evicted), it can skip the comparison above.
 *
 * It falls back to `setData` when the source's contents are not known, or when
 * every feature is new or changed anyway — a full replacement is then the same
 * work and one fewer code path through the worker.
 */
export function setRegionData(map: MapLibreMap, data: DetailLayer | RegionDiff) {
  const source = map.getSource<GeoJSONSource>(REGION_SOURCE_ID)
  if (!source) return

  const labels = map.getSource<GeoJSONSource>(REGION_LABEL_SOURCE_ID)
  const state = held.get(map)

  if (isCollection(data)) {
    const diff = state ? regionDiff(state.features, data) : null

    if (!state || !diff || diff.add.length === data.features.length) {
      const next = holding(data, state)

      placeNames(next, NAMING_INLINE_MS)
      source.setData(data)
      labels?.setData(labelCollection(next))
      held.set(map, next)
      sayRooms(map, next.names.values())
      scheduleNames(map, next)
      return
    }

    applyDiff(map, source, labels, state, diff)
    return
  }

  // A diff against a source whose contents are unknown cannot be tracked, and
  // the next full collection would then be diffed against the wrong thing.
  if (state) applyDiff(map, source, labels, state, data)
}

function applyDiff(
  map: MapLibreMap,
  source: GeoJSONSource,
  labels: GeoJSONSource | undefined,
  state: Held,
  { add = [], remove = [] }: RegionDiff,
) {
  if (add.length === 0 && remove.length === 0) return

  source.updateData({ add, remove })

  for (const slug of remove) drop(state, slug)
  const named = add.flatMap((feature) => take(state, feature) ?? [])
  // A parent already on the map whose child has just arrived moves with this
  // diff, in the one write, rather than a moment after it.
  const moved = placeNames(state, NAMING_INLINE_MS).filter((lettering) => !named.includes(lettering))

  // The names follow the same diff. A feature whose new geometry has nowhere to
  // put a name loses the one it had, which is why it is removed before anything
  // is added rather than only when it is dropped.
  labels?.updateData({
    add: [...named, ...moved].map(labelFeature),
    remove: [...remove, ...add.map(slugOf), ...moved.map((lettering) => slugOf(lettering.feature))],
  })

  sayRooms(map, named)

  scheduleNames(map, state)
}

const slugOf = (feature: DetailFeature) => feature.properties.slug

/**
 * Tells the polygon source how much room each shape has, as a feature-state.
 *
 * The ribbon is capped by it (`regionRibbonPaint`): a ribbon may take up
 * `REGION_RIBBON_ROOM_SHARE` of the largest circle that fits in its shape and
 * no more. The room is already worked out for the name, per shape as it
 * arrives; it reaches the polygon as a STATE rather than as a property so the
 * features are handed to the source exactly as the server sent them — a
 * property would mean a copy of every feature, and a diff against copies.
 *
 * On the Mercator plane: the room is in degrees of latitude on the ground, and
 * a degree of latitude is drawn `1 / cos(latitude)` times as long as a degree
 * of longitude at the equator. Stated that way, one multiplier per zoom turns
 * it into pixels for every shape at once.
 *
 * One call per shape that arrived; a state the source keeps across every later
 * write, so a pan that brought nothing says nothing.
 */
function sayRooms(map: MapLibreMap, names: Iterable<Lettering>) {
  for (const { feature, shape } of names) {
    if (!shape || !(shape.room > 0)) continue

    map.setFeatureState(
      { source: REGION_SOURCE_ID, id: feature.properties.slug },
      { room: Math.round((shape.room / shape.lngScale) * 1e5) / 1e5 },
    )
  }
}

// ---------------------------------------------------------------------------
// A parent's name, clear of its children
// ---------------------------------------------------------------------------

// How long the search may run in the same breath as the data arriving: two
// milliseconds, which is a viewport's worth of parents — measured over the 276
// American appellations at their most detailed, the whole country is thirteen
// milliseconds of it and the slowest single parent under three. Whatever that
// does not reach is left for when the browser is idle.
//
// It is not nothing, on purpose. A name set at its shape's centre and moved a
// moment later is a name that jumps, and a second write to the label source
// after the map had everything it asked for; within the budget, a parent's name
// is written once, where it belongs, with the shapes. The budget is what keeps
// the full search — every candidate point against the parent and each child —
// off the path of the data: it can cost a frame two milliseconds and no more.
const NAMING_INLINE_MS = 2

// How long one sitting of the rest may take before it hands the frame back.
const NAMING_BUDGET_MS = 6

// How long the rest waits for the browser to be idle before it runs anyway.
const NAMING_WAIT_MS = 200

// Answers already worked out, by slug, for the life of the page: the Journal,
// the Wishlist and Sources all unmount the map, and coming back rebuilds it
// over the same appellations. An answer is only good for the outline and the
// children it was worked out for, which is what the signature is.
const FOUND_LIMIT = 4000
const found = new Map<string, { among: string; at: Position | null }>()

function remember(slug: string, among: string, at: Position | null) {
  found.delete(slug)
  found.set(slug, { among, at })
  if (found.size > FOUND_LIMIT) found.delete(found.keys().next().value as string)
}

/** For a test: as if no name had ever been placed on this page. */
export function forgetRegionNames() {
  found.clear()
}

// Enough of an outline to tell it from another tier of the same place: how many
// vertices its largest ring has, and where its box is.
const outlineOf = (slug: string, shape: LabelShape) =>
  `${slug}:${shape.rings[0].length}:${shape.box.west.toFixed(3)},${shape.box.south.toFixed(3)}`

// Closer than this, in degrees, a name has not moved: re-cutting the label
// source's tiles for a shift nobody can see is work for nothing.
const SAME_PLACE = 0.0005

const samePlace = (first: Position, second: Position) =>
  Math.abs(first[0] - second[0]) < SAME_PLACE && Math.abs(first[1] - second[1]) < SAME_PLACE

const now = () => (typeof performance === 'undefined' ? 0 : performance.now())

/**
 * Looks at each waiting name and puts it in the part of its shape that none of
 * its children cover, until `budgetMs` is spent. Returns the names that moved;
 * it writes to no source, so the caller decides whether that is this write or
 * the next.
 *
 * `atLeastOne` is for a sitting that was scheduled: it must get somewhere, or a
 * clock that jumped would have it rescheduling itself for ever.
 *
 * Kept: a parent is not searched again until its outline or its children
 * change, and an answer outlives the map it was found on (`found`).
 */
function placeNames(state: Held, budgetMs: number, atLeastOne = false): Lettering[] {
  // The usual case on a pan: nothing arrived, nothing left, nothing to look at.
  if (state.waiting.size === 0) return []

  const started = now()
  const moved: Lettering[] = []
  let looked = 0

  for (const slug of state.waiting) {
    if (now() - started > budgetMs && !(atLeastOne && looked === 0)) break

    state.waiting.delete(slug)
    looked++

    const lettering = state.names.get(slug)
    if (!lettering?.shape) continue

    const shape = lettering.shape
    const children: LabelShape[] = []
    const signature = [outlineOf(slug, shape)]

    for (const [childSlug, child] of state.names) {
      if (!child.shape || child.tier <= lettering.tier || !overlap(child.shape, shape)) continue
      if (!nestsIn(child.shape, shape)) continue

      children.push(child.shape)
      signature.push(outlineOf(childSlug, child.shape))
    }

    const among = signature.join('|')
    if (lettering.among === among) continue

    const known = found.get(slug)
    const free = known?.among === among ? known.at : childFreeAnchor(shape, children)
    remember(slug, among, free)

    const at = free ?? shape.at
    lettering.among = among
    if (samePlace(at, lettering.at)) continue

    lettering.at = at
    moved.push(lettering)
  }

  return moved
}

function scheduleNames(map: MapLibreMap, state: Held) {
  if (state.scheduled || state.waiting.size === 0) return

  state.scheduled = true

  const run = () => {
    state.scheduled = false
    // Another style, or another record of this one, has taken over since.
    if (held.get(map) !== state) return

    refineRegionNames(map, NAMING_BUDGET_MS)
  }

  // `requestIdleCallback` where there is one, so the rest runs between frames;
  // Safari has none, and there it runs in a task of its own, which is still
  // after the data has been handed to the map and the frame it lands in.
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: NAMING_WAIT_MS })
  } else {
    setTimeout(run, 0)
  }
}

/**
 * The names the inline budget did not reach: looked at when the browser is
 * idle, a sitting at a time, and the label source sent the ones that moved.
 *
 * This is where a set too large to place in two milliseconds is finished — the
 * whole country at a low zoom, on a slow machine — and it stops when `budgetMs`
 * is spent and picks up where it left off. Without a budget, which is how a
 * test calls it, it runs to the end.
 */
export function refineRegionNames(map: MapLibreMap, budgetMs = Infinity) {
  const state = held.get(map)
  if (!state) return

  const moved = placeNames(state, budgetMs, true)

  if (moved.length > 0) {
    // After a style swap, or once the map is gone, there is no source to tell.
    const labels = safely(() => map.getSource<GeoJSONSource>(REGION_LABEL_SOURCE_ID))

    labels?.updateData({
      add: moved.map(labelFeature),
      remove: moved.map((lettering) => slugOf(lettering.feature)),
    })
  }

  scheduleNames(map, state)
}

/**
 * What the map knows about one appellation beyond its own feature: which
 * appellation it sits inside, where its name is set, and its shape as
 * lettering sees it. For the tooltip, which says "AVA in Santa Ynez Valley"
 * and keeps off the name the map has already written.
 *
 * The parent is the DEEPEST shallower-tier appellation that holds this one's
 * own centre — the same test the lettering uses to decide who is whose child.
 * It is asked when the pointer moves onto a different appellation, never per
 * pointer move, and it is one point tested against the few shapes of the
 * tiers above.
 *
 * Null for a slug the map holds no name for.
 */
export function regionSetting(map: MapLibreMap, slug: string) {
  const own = held.get(map)?.names.get(slug)
  if (!own) return null

  let parent: Lettering | null = null

  if (own.shape) {
    for (const other of held.get(map)!.names.values()) {
      if (other === own || !other.shape || other.tier >= own.tier) continue
      if (parent && other.tier <= parent.tier) continue
      if (nestsIn(own.shape, other.shape)) parent = other
    }
  }

  return { parent: parent?.feature.properties.name ?? null, at: own.at, shape: own.shape, whole: own.feature.geometry.type === 'Polygon' }
}

/** Where the map has set an appellation's name, or null when it holds none for that slug. */
export function regionNameAt(map: MapLibreMap, slug: string): Position | null {
  return held.get(map)?.names.get(slug)?.at ?? null
}

// A map that has been removed throws on anything asked of its style.
function safely<Value>(ask: () => Value): Value | undefined {
  try {
    return ask()
  } catch {
    return undefined
  }
}

// ---------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------

const zoomRamp = (...stops: unknown[]) => ['interpolate', ['linear'], ['zoom'], ...stops]
const hovered = ['boolean', ['feature-state', 'hover'], false]
const selected = ['boolean', ['feature-state', 'selected'], false]

// The first of these that exists, and otherwise the slot itself — NOT
// `undefined`. A style that has lost a slot must fail loudly in the map's
// `error` event rather than quietly append a fill on top of the winery dots.
function before(map: MapLibreMap, slot: string, ...preferred: string[]) {
  return preferred.find((id) => map.getLayer(id)) ?? slot
}

// An appellation's level of nesting, as far as the map can tell: the rank of
// its tier among the tiers it has been handed. A nested appellation is never
// served without the one it nests in — they share ground, and the parent's tier
// is the lower — so in practice the ranks are 0, 1, 2 from the first response
// that carries each.
const depthOf = (tiers: number[], tier: number) => Math.max(0, tiers.indexOf(tier))

// One value per tier, as a `match` on the feature's own `min_zoom`. The last
// tier is the fallback as well as a case, so a tier this build of the layer has
// not been told about draws as the deepest it knows rather than not at all.
function byTier(tiers: number[], value: (tier: number, depth: number) => unknown) {
  const last = tiers[tiers.length - 1]

  return [
    'match',
    ['get', 'min_zoom'],
    ...tiers.flatMap((tier) => [tier, value(tier, depthOf(tiers, tier))]),
    value(last, depthOf(tiers, last)),
  ]
}

/**
 * The ribbon layer's paint, for the tiers the map holds.
 *
 * A function of the tiers because every depth is in ONE layer, and a `match`
 * on `min_zoom` is how one layer draws three widths and three tints. It is
 * exported so that what it promises can be checked without a map.
 *
 * - **Width and inset.** `regionRibbonWidth`, sampled at every whole zoom and
 *   interpolated between: a zoom expression has to be the outermost thing in a
 *   paint value, so the curve cannot sit inside the `match`. The inset is half
 *   the width, which puts the ribbon's outer edge on the boundary — a positive
 *   `line-offset` is an inset on a polygon ring.
 * - **Never more than the shape has room for.** A ribbon is at most
 *   `REGION_RIBBON_ROOM_SHARE` of the largest circle that fits in its shape.
 *   A ribbon inset from both sides of a shape narrower than twice its width
 *   meets itself in the middle, and the shape is then FILLED — which is the
 *   film this drawing exists to be rid of, back by another door: between
 *   California and Napa, zooms 7 to 9, every small appellation was a solid
 *   pink patch. Capped, a small shape keeps clean ground in its middle at
 *   every zoom.
 * - **Opacity.** 1 or 0, per feature, once it has arrived: a tier appears at
 *   its own zoom, scrubbed in by `tierReveal`, and the place an open panel
 *   describes hands its ribbon to the amber one. How strong a ribbon LOOKS is
 *   its tint and the layer's opacity, never this.
 * - **Under the pointer** it takes the hover colour and widens, by
 *   feature-state: one repaint, nothing re-cut.
 */
/**
 * 0 to 1 for each tier, from its own zoom, in a layer that holds every tier.
 *
 * WRITTEN FOR HOW A LAYER'S PAINT IS ACTUALLY DRAWN. A paint value that reads
 * the zoom AND the feature is not evaluated at the camera's zoom: MapLibre
 * works it out per feature at the two WHOLE zooms either side and blends the
 * two on the GPU, by the expression's own curve. A stop at "the tier plus
 * 0.35" is therefore never looked at — the renderer asks only about 5 and 6 —
 * and with a linear curve a tier took the whole zoom level to arrive: at 5.6
 * half of California's names, and the ribbons under them, stood at 60% for as
 * long as the camera rested there, a page in two strengths that read as a
 * fault. (A layer per tier has no such trouble — each one's ramp reads the
 * zoom alone — which is why the hairlines and the fills were always right.)
 *
 * So the stops are on the whole zooms the renderer asks about — nothing at a
 * tier's own zoom, everything one zoom on — and the CURVE between them says
 * how soon: an ease that is nine-tenths done `REGION_REVEAL_ZOOMS` past the
 * tier and within two hundredths of whole by six-tenths of a zoom.
 *
 * `wrap` is for a layer with a say of its own on top (the ribbon steps aside
 * for the selection).
 */
function tierReveal(tiers: number[], wrap: (value: unknown) => unknown = (value) => value) {
  const stops: unknown[] = []

  for (let zoom = Math.floor(tiers[0]); zoom <= Math.ceil(tiers[tiers.length - 1]) + 1; zoom++) {
    stops.push(
      zoom,
      wrap(byTier(tiers, (tier) => (tier < zoom ? 1 : 0))),
    )
  }

  return ['interpolate', ['cubic-bezier', 0, 1, REGION_REVEAL_ZOOMS, 1], ['zoom'], ...stops]
}

export function regionRibbonPaint(tiers: number[]): Record<string, unknown> {
  const grow = ['case', hovered, REGION_RIBBON_HOVER_GROW, 1]
  const notSelected = (value: unknown) => ['case', selected, 0, value]
  const zooms: number[] = []

  for (let zoom = Math.floor(tiers[0]); zoom <= MAX_ZOOM; zoom++) zooms.push(zoom)

  // The shape's room in px at a zoom, times the share of it a ribbon may take
  // (`sayRooms`). A shape the map has not been told the room of is not capped.
  const roomy = ['number', ['feature-state', 'room'], 1e6]
  const cap = (zoom: number) => ['*', roomy, Math.round(((512 * 2 ** zoom) / 360) * REGION_RIBBON_ROOM_SHARE * 100) / 100]

  const sized = (share: number) =>
    zoomRamp(
      ...zooms.flatMap((zoom) => [
        zoom,
        [
          'min',
          ['*', grow, byTier(tiers, (tier, depth) => Math.round(regionRibbonWidth(tier, depth, zoom) * share * 100) / 100)],
          share === 1 ? cap(zoom) : ['*', share, cap(zoom)],
        ],
      ]),
    )

  return {
    'line-color': ['case', hovered, REGION_RIBBON_HOVER_COLOR, byTier(tiers, (_tier, depth) => ribbonDepth(depth).tint)],
    'line-width': sized(1),
    'line-offset': sized(0.5),
    'line-opacity': tierReveal(tiers, notSelected),
  }
}

// A tier's fill: the film at rest, and the wash under the pointer, on one ramp.
// The two lists share their first stops; past the last stop the film has, it is
// nothing.
//
// ONLY THE OUTERMOST TIER HAS A FILM AT REST. A nested appellation lies inside
// one that is already tinted, and its own film on top of that was the one
// place the plate still stacked: from zoom 7 to 9, the road every reader takes
// from California down to Napa, the sub-appellations were patches of pink on
// pink while Lodi beside them was clean. Nested, an appellation is its
// hairline and its ribbon from the moment it appears; it still takes the wash
// under the pointer.
function fillOpacity(tier: number, depth: number) {
  const rest = regionFillStops(tier)
  const pointed = regionHoverFillStops(tier)
  const stops: unknown[] = []

  for (let index = 0; index < pointed.length; index += 2) {
    const film = depth === 0 && index < rest.length ? rest[index + 1] : 0
    const wash = pointed[index + 1]

    stops.push(pointed[index], film === wash ? film : ['case', hovered, wash, film])
  }

  return zoomRamp(...stops)
}

// The hairline's width for a tier at a depth, at rest and under the pointer.
function hairlineWidth(tier: number, depth: number) {
  const { hair } = ribbonDepth(depth)
  const stop = (index: 0 | 1) => [
    'case',
    hovered,
    REGION_LINE_HOVER_WIDTH[index],
    Math.round(REGION_LINE_WIDTH[index] * hair * 1000) / 1000,
  ]

  return zoomRamp(tier, stop(0), tier + REGION_LINE_WIDTH_ZOOMS, stop(1))
}

// The three ranks a name can be lettered in (`REGION_LABEL_GREAT_ROOM`).
const room = ['to-number', ['get', 'room'], 0]
const great = ['>=', room, REGION_LABEL_GREAT_ROOM]
const wide = ['>=', room, REGION_LABEL_WIDE_ROOM]
const rank = (capitals: unknown, roomy: unknown, plain: unknown) => ['case', great, capitals, wide, roomy, plain]

// A name longer than this is given the long measure (see the name layer).
const LONG_NAME = 26

// A name's size in px by how far past its tier the camera is, per rank
// (capitals, roomy, plain): the three stops the lettering has always had.
const LABEL_SIZE_STOPS: readonly [number, [number, number, number]][] = [
  [0, [10, 11.5, 11]],
  [2, [11, 13.5, 12]],
  [5, [12.5, 17, 14.5]],
]

function labelSizeAt(past: number, index: 0 | 1 | 2) {
  const stops = LABEL_SIZE_STOPS
  const last = stops[stops.length - 1]
  if (past <= stops[0][0]) return stops[0][1][index]
  if (past >= last[0]) return last[1][index]

  const next = stops.findIndex(([at]) => at > past)
  const [fromAt, from] = stops[next - 1]
  const [toAt, to] = stops[next]

  return Math.round((from[index] + ((to[index] - from[index]) * (past - fromAt)) / (toAt - fromAt)) * 100) / 100
}

/**
 * The name layer's `text-size`, for the tiers the map holds: each name by its
 * rank and by how far past ITS OWN tier the camera is. Sampled at every whole
 * zoom, as the ribbon's width is and for the same reason — and exactly, since
 * every stop of the lettering falls on a whole zoom past a whole tier.
 */
export function regionLabelSize(tiers: number[]) {
  const stops: unknown[] = []

  for (let zoom = Math.floor(tiers[0]); zoom <= MAX_ZOOM; zoom++) {
    stops.push(
      zoom,
      rank(
        byTier(tiers, (tier) => labelSizeAt(zoom - tier, 0)) as never,
        byTier(tiers, (tier) => labelSizeAt(zoom - tier, 1)) as never,
        byTier(tiers, (tier) => labelSizeAt(zoom - tier, 2)) as never,
      ),
    )
  }

  return zoomRamp(...stops)
}

/** The name layer's `text-opacity`: each tier scrubbed in from its own zoom (`tierReveal`). */
export function regionLabelOpacity(tiers: number[]) {
  return tierReveal(tiers)
}

// A name sits on its point unless something placed before it is there — a
// bigger name, a cluster's count — and then it is tried a little to each side
// before it is dropped.
const NUDGE = ['literal', ['center', [0, 0], 'top', [0, 0.7], 'bottom', [0, -0.7], 'left', [0.7, 0], 'right', [-0.7, 0]]]

/**
 * Adds whatever this layer needs that the map does not already have, and returns
 * the tiers it added. (Nothing has to be bound to them: the map's one pointer
 * handler and one click handler ask every tier's fill layer that exists.)
 *
 * Idempotent per tier, which is AE4's acceptance case rather than tidiness. This
 * used to early-return to `setRegionData` whenever the source existed, and build
 * every tier's layers once from the first layer it was handed. That was correct
 * when one fetch delivered every tier at once. Under viewport fetching the first
 * response at zoom 4 carries only `min_zoom <= 4`, so tiers 5 and 7 arrive
 * later and are filtered out by every layer that exists — zooming into Burgundy
 * would draw nothing new and click nothing.
 *
 * Order WITHIN each slot is by tier, for the marks: fills and hairlines
 * ASCENDING, because MapLibre draws in stack order and a sub-appellation's
 * edge belongs on top of the container it sits inside. The ribbons need no
 * stack order — they are one layer, and `line-sort-key` draws a child's after
 * its parent's inside it — which is the other half of KTD13: the half that is
 * left is resolveFeature, which decides which of four nested appellations a
 * click opens. The names need none either: they are one layer, placed largest
 * first (see where it is added).
 *
 * A tier that arrives LATE is inserted where that order puts it, not on top —
 * tier 5 turning up after tier 7 must not bury Margaux's edge under Médoc's.
 */
export function addRegionLayers(map: MapLibreMap, layer: DetailLayer): number[] {
  // A style swap discards the previous style's sources, so this runs again after
  // a basemap failure — but addSource throws on a duplicate id.
  if (map.getSource(REGION_SOURCE_ID)) {
    setRegionData(map, layer)
  } else {
    const state = holding(layer)

    // A parent's name goes clear of its children before the names are written
    // at all, as far as the inline budget reaches.
    placeNames(state, NAMING_INLINE_MS)

    // `promoteId` takes the feature id from the slug already in the properties,
    // which is what hover and selection address a feature by, and what lets
    // `updateData` replace one.
    map.addSource(REGION_SOURCE_ID, { type: 'geojson', data: layer, promoteId: 'slug' })
    map.addSource(REGION_LABEL_SOURCE_ID, { type: 'geojson', data: labelCollection(state), promoteId: 'slug' })
    held.set(map, state)
    sayRooms(map, state.names.values())
    scheduleNames(map, state)
  }

  const state = held.get(map)
  const tiers = regionTiers(layer)
  const added = tiers.filter((tier) => !map.getLayer(regionFillLayerId(tier)))
  // Every tier the map has layers for, with this batch: what depth is ranked in.
  const known = [...new Set([...(state?.tiers ?? []), ...tiers])].sort((first, second) => first - second)
  const grew = known.length !== (state?.tiers.length ?? 0)

  if (state) state.tiers = known
  if (known.length === 0) return added

  // Read before any layer of this batch exists. A tier whose source is already
  // settled has nothing to wait for and is simply drawn; one whose features are
  // still on their way to the worker is added transparent and eased in when
  // they land (see `revealOnArrival`).
  const arriving = !map.isSourceLoaded(REGION_SOURCE_ID)
  const fade = (property: string) =>
    arriving ? { [property]: 0, [`${property}-transition`]: { duration: REGION_ARRIVAL_FADE_MS, delay: 0 } } : {}

  // The ribbons: one layer for every depth, laid down through an offscreen
  // pass (`line-layer-opacity`), so overlaps replace rather than add.
  const ribbonIsNew = !map.getLayer(REGION_RIBBON_LAYER_ID)

  if (ribbonIsNew) {
    map.addLayer(
      {
        id: REGION_RIBBON_LAYER_ID,
        type: 'line',
        source: REGION_SOURCE_ID,
        minzoom: known[0],
        layout: {
          // A mitre, so a ribbon turns a corner as a band of one width; capped
          // low, so a needle-thin spike in a bad outline does not throw a
          // dagger across the map.
          'line-join': 'miter',
          'line-miter-limit': 1.6,
          // Children after their parents: a child's narrow strong ribbon is
          // painted over the strip of its parent's wide pale one that it
          // covers, and the parent shows beyond it.
          'line-sort-key': ['get', 'min_zoom'],
        },
        paint: {
          ...regionRibbonPaint(known),
          'line-layer-opacity': arriving ? 0 : REGION_RIBBON_LAYER_OPACITY,
          ...(arriving ? { 'line-layer-opacity-transition': { duration: REGION_ARRIVAL_FADE_MS, delay: 0 } } : {}),
        } as never,
      },
      SLOT_BAND,
    )
  } else if (grew) {
    // A tier the ribbon was not drawn for. Its paint is a `match` on the tiers,
    // so it is said again; a data-driven paint property is a relayout of the
    // source, which the new tier's features arriving is already causing.
    for (const [property, value] of Object.entries(regionRibbonPaint(known))) {
      map.setPaintProperty(REGION_RIBBON_LAYER_ID, property as never, value as never)
    }
    map.setLayerZoomRange(REGION_RIBBON_LAYER_ID, known[0], 24)

    // The names are one layer too, sized and revealed by a `match` on the
    // tiers, and are told about the new one the same way.
    if (map.getLayer(REGION_LABEL_LAYER_ID)) {
      map.setLayoutProperty(REGION_LABEL_LAYER_ID, 'text-size', regionLabelSize(known) as never)
      map.setPaintProperty(REGION_LABEL_LAYER_ID, 'text-opacity', regionLabelOpacity(known) as never)
      map.setLayerZoomRange(REGION_LABEL_LAYER_ID, known[0], 24)
    }

    // And a tier already drawn may have moved down a depth — a parent tier
    // arriving after its children's. Its hairline's weight goes with it, and
    // so does whether it has a film at rest.
    for (const tier of known) {
      if (added.includes(tier) || !map.getLayer(regionLineLayerId(tier))) continue
      map.setPaintProperty(regionLineLayerId(tier), 'line-width', hairlineWidth(tier, depthOf(known, tier)) as never)
      map.setPaintProperty(regionFillLayerId(tier), 'fill-opacity', fillOpacity(tier, depthOf(known, tier)) as never)
    }
  }

  // THE NAMES: one layer for every tier, like the ribbons, and for a reason of
  // its own — the ORDER names are placed in.
  //
  // Places are identifiable before being clicked, which on a map means a name
  // on the polygon. Drawn from the POINT source: one point, one name, however
  // many tiles the polygon crosses.
  //
  // They were a layer per tier, stacked so that a parent's tier was placed
  // before its children's. But tier is how deep an appellation NESTS, and says
  // nothing about how big it is — and the data's nesting has holes: Crystal
  // Springs of Napa Valley, twenty square kilometres, is recorded as nested in
  // nothing, so it was a tier above the Napa Valley it sits in, was placed
  // first, and at state zoom the map lettered "Crystal Springs of Napa Valley"
  // and had no room left for "Napa Valley". In one layer every name is placed
  // by `symbol-sort-key` alone, and the key is the shape's ROOM: the largest
  // first. That is still parents before children — a shape's largest circle
  // cannot be smaller than one drawn inside it — without trusting the tier,
  // and between unrelated names it is the bigger place that is named.
  //
  // A name still appears with its own tier: the filter holds it back until the
  // camera reaches the tier's zoom (a zoom in a filter is read at whole zooms,
  // and the tiers are whole), and its opacity is scrubbed in from there.
  //
  // Lettered by how much room the shape has (`rank`): capitals in the roman
  // for the great containers, the italic a size up for an appellation with
  // room to spare, plain italic otherwise. The size also grows with
  // `zoom - tier`, so a container that appeared at zoom 4 is lettered larger
  // than the sub-appellation that appeared inside it at 7.
  if (!map.getLayer(REGION_LABEL_LAYER_ID)) {
    map.addLayer(
      {
        id: REGION_LABEL_LAYER_ID,
        type: 'symbol',
        source: REGION_LABEL_SOURCE_ID,
        minzoom: known[0],
        filter: ['<=', ['to-number', ['get', 'min_zoom'], 0], ['zoom']] as never,
        layout: {
          'text-field': ['case', great, ['upcase', ['get', 'name']], ['get', 'name']],
          'text-font': ['case', great, ['literal', REGION_LABEL_CAPS_FONT], ['literal', REGION_LABEL_FONT]],
          'text-size': regionLabelSize(known),
          'text-letter-spacing': rank(0.22, 0.05, 0.02),
          // A long name gets a long measure, so it is two lines and not four:
          // "Antelope Valley of the California High Desert" at the ordinary
          // measure was the tallest lettering in the state.
          'text-max-width': ['case', ['>', ['length', ['get', 'name']], LONG_NAME], 11.5, rank(9, 7, 6.5)],
          'text-line-height': 1.08,
          // More clear ground round a name the further out the camera is: at
          // state zoom the names were set edge to edge, each as loud as the
          // next (`REGION_LABEL_PADDING_STOPS`).
          'text-padding': zoomRamp(...REGION_LABEL_PADDING_STOPS),
          'text-variable-anchor-offset': NUDGE,
          'text-justify': 'center',
          // The name with most room first, so when two collide it is the
          // smaller one that steps aside.
          'symbol-sort-key': ['-', 0, room],
        } as never,
        paint: {
          // The open place's name takes the selection's ink, through
          // feature-state on the label source, so it costs no relayout.
          'text-color': ['case', selected, SELECTED_LINE_COLOR, REGION_LABEL_COLOR] as never,
          'text-halo-color': REGION_LABEL_HALO_COLOR,
          'text-halo-width': 1.6,
          'text-halo-blur': 0.3,
          'text-opacity': regionLabelOpacity(known) as never,
        },
      },
      SLOT_LABEL,
    )
  }

  for (const tier of added) {
    const tierOnly = ['==', ['get', 'min_zoom'], tier] as never
    const deeper = known.filter((other) => other > tier)

    // What a click and the pointer are tested against, and the film at state
    // zoom. Under the next deeper tier if there is one; otherwise under a
    // selected country's glow, which has to stay above every tier; otherwise at
    // the top of the slot.
    map.addLayer(
      {
        id: regionFillLayerId(tier),
        type: 'fill',
        source: REGION_SOURCE_ID,
        minzoom: tier,
        filter: tierOnly,
        paint: {
          'fill-color': REGION_FILL_COLOR,
          // A function of the zoom, and of the pointer: the film while the
          // tier is state-sized and nothing after it, and a wash under the
          // pointer for as long as a shape of this tier is seen whole. Per
          // feature — never `fill-layer-opacity`, which is the arrival fade's.
          'fill-opacity': fillOpacity(tier, depthOf(known, tier)) as never,
          // The hairline owns the edge. The antialias ring was a second, fainter
          // line doubling it.
          'fill-antialias': false,
          ...fade('fill-layer-opacity'),
        },
      },
      before(map, SLOT_TINT, ...deeper.map(regionFillLayerId), SELECTED_GLOW_LAYER_ID),
    )

    // The edge itself. Over the roads and under every label, with the deeper
    // tiers' on top of it. Quiet where a tier first appears, nearly full
    // strength a couple of levels in, and gone for the selected place, whose
    // edge is the amber one.
    map.addLayer(
      {
        id: regionLineLayerId(tier),
        type: 'line',
        source: REGION_SOURCE_ID,
        minzoom: tier,
        filter: tierOnly,
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': ['case', hovered, REGION_HOVER_COLOR, REGION_LINE_COLOR] as never,
          'line-width': hairlineWidth(tier, depthOf(known, tier)) as never,
          'line-opacity': zoomRamp(
            tier,
            0,
            tier + REGION_REVEAL_ZOOMS,
            ['case', selected, 0, hovered, 1, REGION_LINE_OPACITY],
            tier + REGION_LINE_NEAR_ZOOMS,
            ['case', selected, 0, hovered, 1, REGION_LINE_NEAR_OPACITY],
          ) as never,
          ...fade('line-layer-opacity'),
        },
      },
      before(map, SLOT_LINE, ...deeper.map(regionLineLayerId)),
    )
  }

  if (arriving && (added.length > 0 || ribbonIsNew)) revealOnArrival(map, added, ribbonIsNew)

  return added
}

/**
 * Eases in tiers that were added transparent, once their features have landed.
 *
 * The zoom ramps cover a tier the camera crosses with its data already loaded.
 * This covers the other pop: a tier whose layers are created because a response
 * just brought its first features. Raising the opacity in the same tick as
 * `addLayer` snaps — the transition runs out while the worker is still cutting
 * tiles — so it waits for the source to report loaded.
 *
 * The ribbon layer is eased in with the first tier it is created for, to the
 * opacity it is laid at for good. A tier that joins it later is not: the layer
 * is already drawing its parents, and there is no fading part of a layer.
 *
 * On the fills and hairlines `*-layer-opacity` costs an offscreen pass only
 * while it is below 1, so nothing is paid once the fade is done. It is not what
 * reveals a tier by zoom and must not become it.
 */
function revealOnArrival(map: MapLibreMap, tiers: number[], ribbon: boolean) {
  const onData = (event: MapSourceDataEvent) => {
    if (event.sourceId !== REGION_SOURCE_ID || !map.isSourceLoaded(REGION_SOURCE_ID)) return

    map.off('sourcedata', onData)

    for (const tier of tiers) {
      // A style swap in the meantime took the layers with it; the redraw added
      // fresh ones with a listener of their own.
      if (!map.getLayer(regionFillLayerId(tier))) continue

      map.setPaintProperty(regionFillLayerId(tier), 'fill-layer-opacity', 1)
      map.setPaintProperty(regionLineLayerId(tier), 'line-layer-opacity', 1)
    }

    if (ribbon && map.getLayer(REGION_RIBBON_LAYER_ID)) {
      map.setPaintProperty(REGION_RIBBON_LAYER_ID, 'line-layer-opacity', REGION_RIBBON_LAYER_OPACITY)
    }
  }

  map.on('sourcedata', onData)
}

export function setRegionVisibility(map: MapLibreMap, tiers: number[], visible: boolean) {
  for (const id of regionLayerIds(tiers)) {
    if (!map.getLayer(id)) continue
    map.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none')
  }
}

// ---------------------------------------------------------------------------
// The selected place
// ---------------------------------------------------------------------------

export type SelectionFeature = Feature<Geometry, Record<string, unknown>>

const isVeil = ['==', ['get', 'part'], SELECTED_PART_VEIL]
const edgeOfCountry = ['all', ['!=', ['get', 'part'], SELECTED_PART_VEIL], ['==', ['get', 'kind'], COUNTRY_KIND]]
const edgeOfAppellation = ['all', ['!=', ['get', 'part'], SELECTED_PART_VEIL], ['!=', ['get', 'kind'], COUNTRY_KIND]]

/**
 * The layers that draw the place an open panel describes, from a source of its
 * own that holds that place and the world with the place cut out of it.
 *
 * Selection used to rewrite a data-driven `case` on every tier's fill and line
 * and on both country layers. MapLibre answers a data-driven `setPaintProperty`
 * with a relayout, so every open and every close re-cut every tile of the
 * region source and the world source — 84 ms and six tile reloads per click,
 * exactly when the camera starts needing new tiles. A two-feature `setData` on
 * a source nothing else reads touches neither.
 *
 * What selected LOOKS like (`SELECTED_VEIL_COLOR` in map_config.ts has why):
 *
 * - the VEIL, over everything that is not the place: over the roads and every
 *   other appellation's hairline, under every label and dot;
 * - for an appellation, an opaque amber RIBBON inside its boundary, an amber
 *   LINE on it and a paper CASING outside that. Its own claret ribbon and
 *   hairline are switched off by the `selected` feature-state the map sets on
 *   the region source, so the amber is not printed over them;
 * - nothing on the ground inside. Whatever nests in the selected place is drawn
 *   exactly as it was, at full strength, on the one part of the screen the veil
 *   leaves alone.
 *
 * A COUNTRY gets the veil and a soft amber glow along the inside of its edge,
 * under the water, and no line. Its polygon is simplified to about 22 km, and a
 * cased line traces that error across Brittany and the Gironde; the soft
 * version, trimmed by the sea because it is under the water layer, does not.
 *
 * Added with the style, before any region tier, so every tier's fill is
 * inserted beneath the glow (see `addRegionLayers`); the hairlines need no such
 * care, because the selection has a slot of its own above theirs.
 */
export function addSelectionLayers(map: MapLibreMap, feature: SelectionFeature | null) {
  if (map.getSource(SELECTED_SOURCE_ID)) {
    setSelection(map, feature)
    return
  }

  map.addSource(SELECTED_SOURCE_ID, { type: 'geojson', data: selectionCollection(feature) })

  // Inset by half its width, so its outer edge sits on the boundary, and
  // blurred by about two thirds of it. All three follow one set of stops, so
  // the glow keeps its proportions as it grows with the zoom.
  const glow = (share: number) =>
    zoomRamp(...SELECTED_GLOW_WIDTH_STOPS.map((value, index) => (index % 2 === 0 ? value : value * share)))

  map.addLayer(
    {
      id: SELECTED_GLOW_LAYER_ID,
      type: 'line',
      source: SELECTED_SOURCE_ID,
      filter: edgeOfCountry as never,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': SELECTED_FILL_COLOR,
        'line-width': glow(1) as never,
        'line-offset': glow(0.5) as never,
        'line-blur': glow(0.65) as never,
        'line-opacity': SELECTED_GLOW_OPACITY,
      },
    },
    SLOT_TINT,
  )

  map.addLayer(
    {
      id: SELECTED_VEIL_LAYER_ID,
      type: 'fill',
      source: SELECTED_SOURCE_ID,
      filter: isVeil as never,
      paint: {
        'fill-color': SELECTED_VEIL_COLOR,
        'fill-opacity': SELECTED_VEIL_OPACITY,
        // Its edge is under the casing and the line. Antialiased, a fill draws
        // a one-pixel outline over its own edge, which at an eighth is a
        // darker hairline round the place.
        'fill-antialias': false,
      },
    },
    SLOT_HIGHLIGHT,
  )

  map.addLayer(
    {
      id: SELECTED_CASING_LAYER_ID,
      type: 'line',
      source: SELECTED_SOURCE_ID,
      filter: edgeOfAppellation as never,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': SELECTED_CASING_COLOR,
        'line-width': zoomRamp(...SELECTED_CASING_WIDTH_STOPS) as never,
        'line-opacity': 0.95,
      },
    },
    SLOT_HIGHLIGHT,
  )

  // The claret ribbon's place, taken: the same inset, opaque, in amber.
  const inset = SELECTED_RIBBON_WIDTH_STOPS.map((value, index) => (index % 2 === 0 ? value : value / 2))

  map.addLayer(
    {
      id: SELECTED_RIBBON_LAYER_ID,
      type: 'line',
      source: SELECTED_SOURCE_ID,
      filter: edgeOfAppellation as never,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': SELECTED_FILL_COLOR,
        'line-width': zoomRamp(...SELECTED_RIBBON_WIDTH_STOPS) as never,
        'line-offset': zoomRamp(...inset) as never,
      },
    },
    SLOT_HIGHLIGHT,
  )

  map.addLayer(
    {
      id: SELECTED_LINE_LAYER_ID,
      type: 'line',
      source: SELECTED_SOURCE_ID,
      filter: edgeOfAppellation as never,
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': SELECTED_LINE_COLOR,
        'line-width': zoomRamp(...SELECTED_LINE_WIDTH_STOPS) as never,
      },
    },
    SLOT_HIGHLIGHT,
  )
}

// The whole world, as one ring: what the veil is before the place is cut out of
// it. To the edge of the projection, not the pole.
const WORLD: Position[] = [[-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85]]

// The outer ring of every part of a place: the holes the veil has.
function outerRings(geometry: Geometry): Position[][] | null {
  if (geometry.type === 'Polygon') return geometry.coordinates.slice(0, 1)
  if (geometry.type === 'MultiPolygon') return geometry.coordinates.flatMap((polygon) => polygon.slice(0, 1))

  return null
}

/**
 * What the selection source holds for a place: the world with the place cut
 * out of it, and the place. The place is the feature it was handed, untouched —
 * its properties are what the edge layers filter on — and the veil's holes are
 * that feature's own rings, by reference, so a detailed outline is not copied
 * to be selected.
 *
 * Untouched unless its outline has NEEDLE SPIKES, which a handful of the
 * source's boundaries do. Then both parts are cut from a copy with the spikes
 * taken off (despike.ts): an opaque amber ribbon run up and down every needle
 * of a bad outline was the loudest thing on the page, and it was a data error.
 * The copy is made once per geometry and remembered, so a selection that is
 * re-run by a pan costs a lookup. An appellation only.
 *
 * A place with no area has an edge and no veil: there is nothing to cut out.
 */
export function selectionCollection(feature: SelectionFeature | null): FeatureCollection {
  if (!feature) return { type: 'FeatureCollection', features: [] }

  // Not a country: its edge is a soft band under the water, which shows no
  // spike, and its outline is thousands of vertices of real coast.
  const geometry = feature.properties.kind === COUNTRY_KIND ? feature.geometry : despiked(feature.geometry)
  const place = geometry === feature.geometry ? feature : { ...feature, geometry }
  const holes = outerRings(geometry)
  if (!holes) return { type: 'FeatureCollection', features: [place] }

  const veil: Feature<Polygon> = {
    type: 'Feature',
    properties: { part: SELECTED_PART_VEIL },
    geometry: { type: 'Polygon', coordinates: [WORLD, ...holes] },
  }

  return { type: 'FeatureCollection', features: [veil, place] }
}

export function setSelection(map: MapLibreMap, feature: SelectionFeature | null) {
  map.getSource<GeoJSONSource>(SELECTED_SOURCE_ID)?.setData(selectionCollection(feature))
}
